import { useContext, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { Link } from "react-router-dom";
import { useOpenState, SettingsReadOnly, ViewOnlyPill, LockedTile, explainLocked } from "@/components/SettingsSection";
import { toast } from "sonner";
import { ChevronDown,
  MessageSquareText, Headset, ShieldAlert, Clock, Plus, Trash2, Save,
  CheckCircle2, XCircle, Lock, Building2, AlertTriangle, Megaphone, ClipboardCheck,
} from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { PricingBadge } from "@/components/CapabilityBadge";
import { V } from "@/lib/vocab";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Toggle({ checked, onChange, testid }) {
  return (
    <button data-testid={testid} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}
      className={`relative h-5 w-9 rounded-full transition-colors shrink-0 shadow-sm ${checked ? "bg-redline-500" : "bg-sand-300"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

function CoverageEditor({ cov, onChange, idPrefix }) {
  const set = (patch) => onChange({ ...cov, ...patch });
  const windows = cov.windows || [];
  const setWindows = (w) => set({ windows: w });
  const toggleDay = (i, d) => {
    const w = windows.map((x, j) => j !== i ? x : { ...x, days: x.days.includes(d) ? x.days.filter((y) => y !== d) : [...x.days, d] });
    setWindows(w);
  };
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <Toggle testid={`${idPrefix}-247`} checked={!!cov.is_24_7} onChange={(v) => set({ is_24_7: v })} />
        <span className="text-sm font-semibold flex items-center gap-1.5"><Clock className="h-4 w-4 text-redline-500" /> Around the clock — 24/7</span>
      </label>
      {!cov.is_24_7 && (
        <div className="space-y-3 pt-1">
          <p className="text-sm text-muted-foreground">Define the weekly shifts your team covers. Outside these hours the bot is honest that a person is off shift.</p>
          {windows.map((w, i) => (
            <div key={i} className="rounded-xl border border-sand-200 p-4 space-y-3" data-testid={`${idPrefix}-window-${i}`}>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => (
                  <button key={d} onClick={() => toggleDay(i, d)}
                    className={`h-8 w-11 rounded-lg text-xs font-bold transition-colors ${w.days.includes(d) ? "bg-ink text-white" : "bg-sand-100 text-ink-700 hover:bg-sand-200"}`}>{d}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input type="time" value={w.start} onChange={(e) => setWindows(windows.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} className="rounded-lg border border-sand-200 px-3 py-1.5" />
                <span className="text-muted-foreground">to</span>
                <input type="time" value={w.end} onChange={(e) => setWindows(windows.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} className="rounded-lg border border-sand-200 px-3 py-1.5" />
                <button onClick={() => setWindows(windows.filter((_, j) => j !== i))} className="ml-auto text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <button data-testid={`${idPrefix}-add-window`} onClick={() => setWindows([...windows, { days: ["Mon", "Tue", "Wed", "Thu", "Fri"], start: "17:00", end: "23:59" }])}
            className="flex items-center gap-1.5 text-sm font-semibold text-redline-600 hover:underline"><Plus className="h-4 w-4" /> Add a shift</button>
        </div>
      )}
      <div>
        <label className="text-sm font-semibold">Timezone</label>
        <input value={cov.timezone || ""} onChange={(e) => set({ timezone: e.target.value })}
          className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm" placeholder="America/Chicago" />
      </div>
    </div>
  );
}

function LiveBadge({ status }) {
  if (!status?.module_on) return null;
  return (
    <span className={`text-xs font-semibold flex items-center gap-1.5 px-3 py-1 rounded-full ${status.live_now ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
      {status.live_now ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {status.live_now ? "Live now" : "Off shift"}
    </span>
  );
}

function ModuleCard({ icon: Icon, name, desc, help, children, enabled, onToggle, status, alwaysOn, testid, badge, compact }) {
  const [open, toggleOpen] = useOpenState(testid, false);
  const readOnly = useContext(SettingsReadOnly);
  const hasBody = Boolean(children) && Boolean(enabled);
  return (
    <div className={`bg-white border overflow-hidden ${readOnly ? "border-sand-300" : "border-sand-200"} ${compact ? "rounded-xl" : "rounded-2xl"} ${!enabled && !alwaysOn ? "opacity-60" : ""}`} data-testid={testid} data-locked={readOnly || undefined}>
      <div role={hasBody ? "button" : undefined} tabIndex={hasBody ? 0 : undefined} aria-expanded={hasBody ? open : undefined} data-testid={`${testid}-header`}
        onClick={(e) => { if (readOnly && explainLocked(e, e.currentTarget, readOnly)) return; if (hasBody) toggleOpen(); }}
        onKeyDown={hasBody ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleOpen(); } } : undefined}
        className={`${compact ? "p-4" : "p-4 sm:p-5 md:px-6"} flex items-start gap-3 ${readOnly ? "bg-sand-50/80" : ""} ${hasBody ? `cursor-pointer select-none ${readOnly ? "hover:bg-sand-100/70" : "hover:bg-sand-50"}` : ""} ${hasBody && open ? "border-b border-sand-100" : ""}`}>
        <LockedTile locked={readOnly}><div className={`h-9 w-9 rounded-xl flex items-center justify-center ${readOnly ? "bg-sand-100" : enabled || alwaysOn ? "bg-redline-50" : "bg-sand-100"}`}>
          <Icon className={`h-[18px] w-[18px] ${readOnly ? "text-ink/60" : enabled || alwaysOn ? "text-redline-500" : "text-ink-400"}`} strokeWidth={1.5} />
        </div></LockedTile>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[15px] flex items-center gap-2 flex-wrap">{name}
            {readOnly && <ViewOnlyPill testid={`${testid}-view-only`} reason={readOnly} />}
            {badge} {help && <InfoTip>{help}</InfoTip>} <LiveBadge status={status} /></div>
          <p className={`text-sm text-muted-foreground mt-0.5 ${open ? "" : "line-clamp-2"}`}>{desc}</p>
        </div>
        <span className={`shrink-0 flex items-center gap-2 ${readOnly ? "rl-view-only" : ""}`} onClick={(e) => { if (!readOnly) e.stopPropagation(); }} onKeyDown={(e) => e.stopPropagation()}>
          {alwaysOn ? (
            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1 hidden sm:flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Always on</span>
          ) : (
            <Toggle testid={`${testid}-toggle`} checked={enabled} onChange={onToggle} />
          )}
        </span>
        {hasBody && <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />}
      </div>
      {hasBody && open && <div className={`${compact ? "p-4" : "p-4 sm:p-5 md:px-6"} space-y-5`}>{children}</div>}
    </div>
  );
}

export default function Modules({ embedded, sections, compact }) {
  const show = (id) => !sections || sections.includes(id);
  const { tenantId, tenant, refreshTenants, role } = useDash();
  const isSuper = role === "super_admin";
  const [sup, setSup] = useState(null);
  const [sec, setSec] = useState(null);
  const [concierge, setConcierge] = useState(true);
  const [bc, setBc] = useState(false);
  const [supStatus, setSupStatus] = useState(null);
  const [secStatus, setSecStatus] = useState(null);
  const [saving, setSaving] = useState("");
  const [cl, setCl] = useState(null);           // Checklists module settings

  useEffect(() => {
    if (!tenant) return;
    setConcierge(tenant.concierge_enabled !== false);
    setBc(tenant.broadcasts_enabled === true);
    setSup(JSON.parse(JSON.stringify(tenant.support_module || { enabled: false, coverage: { timezone: "America/Chicago", is_24_7: false, windows: [] }, routing: { notify: [] }, off_hours_message: "" })));
    setSec(JSON.parse(JSON.stringify(tenant.security_module || { enabled: false, tier: "T1", coverage: { timezone: "America/Chicago", is_24_7: false, windows: [] }, on_call_escalation: { enabled: false, notify: [] }, monitoring_routing: { desk_id: "", notify: [] }, alerts: { enabled: true, sender_persona: "Redline Safety" }, off_hours_message: "" })));
    if (show("support")) api.get(`/tenants/${tenantId}/support/status`).then((r) => setSupStatus(r.data)).catch(() => {});
    if (show("security")) api.get(`/tenants/${tenantId}/security/status`).then((r) => setSecStatus(r.data)).catch(() => {});
    if (show("checklists")) api.get(`/tenants/${tenantId}/checklists/config`).then((r) => setCl(r.data.config)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tenant]);

  if (!sup || !sec) return null;

  const supWindows = sup.coverage?.windows || [];
  const supHasCoverage = !!sup.coverage?.is_24_7 || supWindows.some((w) => w.days?.length && w.start && w.end);
  const supNeedsCoverage = sup.enabled && !supHasCoverage;

  const saveChecklists = async (patch) => {
    setSaving("checklists");
    try {
      const r = await api.put(`/tenants/${tenantId}/checklists/config`, patch ?? cl);
      if (r.data.config) setCl(r.data.config);
      await refreshTenants?.();
      toast.success("Checklists saved");
    } catch { toast.error("Couldn't save Checklists"); } finally { setSaving(""); }
  };

  const saveConcierge = async (v) => {
    if (!v && !window.confirm(`Turn OFF Core Concierge?

The AI will stop answering ${V.lcs("person")} entirely. Every incoming text will be routed straight to a live operator in the Live Support queue, with no automatic reply. Turn it back on to restore AI answers.

Continue?`)) return;
    setConcierge(v);
    try { await api.put(`/tenants/${tenantId}/concierge`, { enabled: v }); await refreshTenants?.(); toast.success(v ? "Core Concierge is on" : "Core Concierge off — every text now routes to a live operator"); }
    catch (e) { setConcierge(!v); toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const bcEntitled = !!(tenant?.broadcasts_entitled);
  const saveBroadcasts = async (v) => {
    setBc(v);
    try { await api.put(`/tenants/${tenantId}/service`, { broadcasts_enabled: v }); await refreshTenants?.(); toast.success(v ? "Messages is on — the Messages tab is now in the sidebar" : "Messages off — the Messages tab is hidden"); }
    catch (e) { setBc(!v); toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  // Super admins ARE Redline — they can enable the premium add-on right here (sets the
  // entitlement + turns it on). Managers on a non-entitled community see the "contact Redline" note.
  const enableBroadcastsAddon = async () => {
    setBc(true);
    try {
      await api.put(`/tenants/${tenantId}/entitlements`, { entitlements: { broadcasts: true } });
      await api.put(`/tenants/${tenantId}/service`, { broadcasts_enabled: true });
      await refreshTenants?.();
      toast.success(`Messages add-on enabled for this ${V.lc("org")}`);
    } catch (e) { setBc(false); toast.error(e?.response?.data?.detail || "Could not enable the add-on"); }
  };
  const saveSupport = async () => {
    if (sup.enabled && !supHasCoverage) { toast.error("Turn on 24/7 or add at least one shift window before enabling Live Support."); return; }
    setSaving("support");
    try { const r = await api.put(`/tenants/${tenantId}/support`, sup); setSupStatus(r.data.status); await refreshTenants?.(); toast.success("Live Support saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving("");
  };
  const saveSecurity = async () => {
    setSaving("security");
    try { const r = await api.put(`/tenants/${tenantId}/security`, sec); setSecStatus(r.data.status); await refreshTenants?.(); toast.success("Security saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving("");
  };

  const notifyInput = (val, onSet, testid, placeholder) => (
    <div>
      <label className="text-sm font-semibold">Notify (emails)</label>
      <input data-testid={testid} value={(val || []).join(", ")} placeholder={placeholder}
        onChange={(e) => onSet(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
        className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm" />
    </div>
  );
  const offHours = (val, onSet, testid) => (
    <div>
      <label className="text-sm font-semibold flex items-center gap-1.5">Off-hours message to {V.lcs("person")} <InfoTip>This is what the bot texts {V.a("person")} when this module is outside the covered shift hours you set above. It keeps the concierge honest that a live person is off shift right now, instead of implying someone is available. Leave blank to use a sensible default.</InfoTip></label>
      <textarea data-testid={testid} value={val || ""} rows={2} onChange={(e) => onSet(e.target.value)}
        placeholder="e.g. Our team is off shift right now — I've logged this and someone will follow up first thing tomorrow."
        className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm resize-none" />
      <p className="text-xs text-muted-foreground mt-1">The bot uses this, honestly, when this module is off shift.</p>
    </div>
  );
  const SaveBtn = ({ onClick, busy, disabled }) => (
    <button onClick={onClick} disabled={!!saving || disabled}
      className="flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
      <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
    </button>
  );

  return (
    <div className={compact ? "space-y-3" : "space-y-6"}>
      {!embedded && (
        <div>
          <PageTitle title="Module Control Center" about={<><Building2 className="h-4 w-4 text-redline-500 inline -mt-0.5 mr-1.5" />
            Configure the features live for <span className="font-semibold text-ink">{tenant?.name}</span>. Each {V.lc("org")} is provisioned independently.</>} />
        </div>
      )}

      {show("core") && <ModuleCard icon={MessageSquareText} name="Core Concierge" testid="module-core" compact={compact}
        badge={<span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-emerald-50 text-emerald-700">Core</span>}
        help={`The AI concierge itself. ON = the AI answers ${V.lcs("person")} automatically from your documents and rules, and only hands off when someone asks for a person or a guardrail trips. OFF = no AI at all; every incoming text goes straight to a live operator.`}
        desc={`The concierge answers ${V.lc("person")} questions from your documents and rules — grounded answers, ${V.lc("request")} intake, 24/7 by text and phone.`}
        enabled={concierge} onToggle={saveConcierge}>
        <p className="text-sm text-muted-foreground">
          When <span className="font-semibold text-ink">on</span>, the AI answers {V.lcs("person")} automatically and only hands off when someone asks for a person or a guardrail trips. Turn it <span className="font-semibold text-ink">off</span> to send every incoming text straight to a live operator (the Live Support queue) with no AI reply.
        </p>
      </ModuleCard>}

      {show("broadcasts") && <ModuleCard icon={Megaphone} name="Messages & Announcements" testid="module-broadcasts" compact={compact}
        badge={<PricingBadge addonKey="broadcasts" />}
        help={`Premium add-on. Text or email everyone, a saved group, or a single ${V.lc("person")} — now or scheduled — from the Messages page.`}
        desc={`Announcements, group messages and 1:1 texts/emails to ${V.lcs("person")}.`}
        enabled={bcEntitled ? bc : false}
        onToggle={(v) => {
          if (bcEntitled) return saveBroadcasts(v);
          if (isSuper) { if (v) return enableBroadcastsAddon(); return; }
          toast.info("Messages & Announcements is a premium add-on — contact Ask Redline to enable it.");
        }}>
        {bcEntitled ? (
          <p className="text-sm text-muted-foreground">
            When <span className="font-semibold text-ink">on</span>, the <span className="font-semibold text-ink">Messages</span> tab appears in the sidebar so you can text or email everyone, a group, or one {V.lc("person")}. Turn it off to hide the tab.
          </p>
        ) : (
          <div data-testid="broadcasts-locked" className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
            <span>Messages &amp; Announcements is a <span className="font-semibold">premium add-on</span>{isSuper ? ` — flip the switch to enable it for this ${V.lc("org")} (billing follows the Plans & Add-ons list price).` : ` that isn't enabled for this ${V.lc("org")} yet. Contact Ask Redline to turn it on.`}</span>
          </div>
        )}
      </ModuleCard>}

      {show("support") && <ModuleCard icon={Headset} name="Live Support Center" testid="module-support" compact={compact}
        badge={<PricingBadge addonKey="after_hours_operator" />}
        help={`A paid add-on where Redline's call-center operators can join ${V.lc("person")} text threads during the hours you set. It feeds the Operator Console. Leave off if this ${V.lc("org")} is self-managed and doesn't pay for live support.`}
        desc={`Redline's call center can join ${V.lc("person")} threads. Feeds the Operator Console.`}
        enabled={sup.enabled} status={supStatus} onToggle={(v) => setSup({ ...sup, enabled: v })}>
        <CoverageEditor idPrefix="support" cov={sup.coverage} onChange={(c) => setSup({ ...sup, coverage: c })} />
        {notifyInput(sup.routing?.notify, (n) => setSup({ ...sup, routing: { ...(sup.routing || {}), notify: n } }), "support-notify", "ops@redlinemonitoring.com")}
        {offHours(sup.off_hours_message, (v) => setSup({ ...sup, off_hours_message: v }), "support-offhours")}
        {supNeedsCoverage && (
          <div data-testid="support-coverage-warning" className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
            <span>Live Support has <span className="font-semibold">no coverage defined</span>, so {V.lcs("person")} would always be told the team is off shift. Turn on <span className="font-semibold">24/7</span> or add at least one <span className="font-semibold">shift window</span> above to save.</span>
          </div>
        )}
        <SaveBtn onClick={saveSupport} busy={saving === "support"} disabled={supNeedsCoverage} />

        <div className="pt-5 mt-1 border-t border-sand-200 text-sm text-muted-foreground">
          Operator quick replies now live with the other conversation settings —{" "}
          <Link to="/dashboard/settings?tab=channels&section=quick-replies-section" data-testid="support-quick-replies-link" className="pointer-events-auto font-semibold text-redline-600 hover:underline">Channels → Conversations → Quick replies</Link>.
        </div>
      </ModuleCard>}

      {show("security") && <ModuleCard icon={ShieldAlert} name="Security Monitoring" testid="module-security" compact={compact}
        help={`A premium add-on where live operators monitor incidents, check cameras and dispatch. Pick the tier and coverage below. Leave off unless this ${V.lc("org")} pays for monitoring.`}
        desc="Live operators monitor incidents, check cameras and dispatch. Premium."
        enabled={sec.enabled} status={secStatus} onToggle={(v) => setSec({ ...sec, enabled: v })}>
        <div>
          <label className="text-sm font-semibold">Monitoring tier</label>
          <select data-testid="security-tier" value={sec.tier} onChange={(e) => setSec({ ...sec, tier: e.target.value })}
            className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white">
            <option value="T1">T1 — Log &amp; queue only (no live watch)</option>
            <option value="T2">T2 — Live operators during covered hours</option>
            <option value="T2_24_7">T2 — Live operators, 24/7</option>
          </select>
        </div>
        {sec.tier === "T2" && <CoverageEditor idPrefix="security" cov={sec.coverage} onChange={(c) => setSec({ ...sec, coverage: c })} />}
        <label className="flex items-center gap-3 cursor-pointer">
          <Toggle testid="security-oncall" checked={!!sec.on_call_escalation?.enabled} onChange={(v) => setSec({ ...sec, on_call_escalation: { ...(sec.on_call_escalation || {}), enabled: v } })} />
          <span className="text-sm font-semibold">On-call escalation for high-severity incidents</span>
        </label>
        {sec.on_call_escalation?.enabled && notifyInput(sec.on_call_escalation?.notify, (n) => setSec({ ...sec, on_call_escalation: { ...(sec.on_call_escalation || {}), notify: n } }), "security-oncall-notify", "oncall@redlinemonitoring.com")}
        {notifyInput(sec.monitoring_routing?.notify, (n) => setSec({ ...sec, monitoring_routing: { ...(sec.monitoring_routing || {}), notify: n } }), "security-notify", "desk@redlinemonitoring.com")}
        <div>
          <label className="text-sm font-semibold">Alert sender persona</label>
          <input data-testid="security-persona" value={sec.alerts?.sender_persona || ""} onChange={(e) => setSec({ ...sec, alerts: { ...(sec.alerts || {}), sender_persona: e.target.value } })}
            className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm" placeholder="Redline Safety" />
        </div>
        {offHours(sec.off_hours_message, (v) => setSec({ ...sec, off_hours_message: v }), "security-offhours")}
        <SaveBtn onClick={saveSecurity} busy={saving === "security"} />
      </ModuleCard>}

      {show("checklists") && cl && <ModuleCard icon={ClipboardCheck} name="Checklists & Recurring Work" testid="module-checklists" compact={compact}
        help="The recurring work you already pay for — cleaning, pool, landscaping, monthly walks. Whoever does it gets one permanent link; it opens whatever is due that day, they tick it off with photos, and every scheduled visit becomes a record whether or not anyone shows up. Its own module: nothing else has to be switched on first."
        desc="Recurring schedules · photo proof · a missed-visit record · goes to a company or to one person by name and mobile."
        enabled={cl.enabled === true} onToggle={(v) => saveChecklists({ ...cl, enabled: v })}>
        <p className="text-sm text-muted-foreground">
          These are the community defaults. Any checklist can override its own <span className="font-semibold text-ink">done-by time</span>,
          <span className="font-semibold text-ink"> reminder</span>, <span className="font-semibold text-ink">who gets the texts</span> and
          <span className="font-semibold text-ink"> auto-approve</span> — edit it on the <Link to="/dashboard/checklists" className="font-semibold text-redline-600 hover:underline" data-testid="checklists-page-link">Checklists</Link> page.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-semibold">Done by, when a checklist doesn't set one</label>
            <p className="text-xs text-muted-foreground">The deadline a visit is judged against. Nothing is tied to a fixed hour of the morning — vendors arrive whenever they arrive.</p>
            <input type="time" data-testid="checklists-due-by" value={cl.due_by || "17:00"}
              onChange={(e) => setCl({ ...cl, due_by: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-sm font-semibold">Reminder to the vendor</label>
            <p className="text-xs text-muted-foreground">When their link gets texted out for the day.</p>
            <select data-testid="checklists-remind" value={cl.remind || "window_start"}
              onChange={(e) => setCl({ ...cl, remind: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white">
              <option value="window_start">When the service window opens</option>
              <option value="hours_before">A few hours before the deadline</option>
              <option value="off">Don't remind them at all</option>
            </select>
          </div>
          {cl.remind === "hours_before" && (
            <div>
              <label className="text-sm font-semibold">How many hours before</label>
              <p className="text-xs text-muted-foreground">Counted back from the done-by time.</p>
              <input type="number" min="1" max="12" data-testid="checklists-remind-hours" value={cl.remind_hours ?? 2}
                onChange={(e) => setCl({ ...cl, remind_hours: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white" />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold">Nudge if nothing has been ticked</label>
            <p className="text-xs text-muted-foreground">Minutes before the deadline. The nudge goes to the vendor only — you aren't paged for a late start.</p>
            <input type="number" min="0" max="480" data-testid="checklists-nudge" value={cl.nudge_minutes ?? 60}
              onChange={(e) => setCl({ ...cl, nudge_minutes: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-sm font-semibold">Who gets the texts</label>
            <p className="text-xs text-muted-foreground">Set per vendor on the checklist itself. This is the starting point.</p>
            <select data-testid="checklists-notify" value={cl.notify_to || "both"}
              onChange={(e) => setCl({ ...cl, notify_to: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white">
              <option value="both">The vendor's office and the person on site</option>
              <option value="office">The vendor's office only</option>
              <option value="onsite">The person on site only</option>
            </select>
          </div>
        </div>

        <div className="space-y-3 pt-1">
          {[["photo_required_default", "New tasks require a photo", "Tick this and every task you add starts out needing photo proof. You can still turn it off task by task."],
            ["mark_missed", "Mark a visit missed when the window closes", "Nothing ticked by the deadline files a missed record and texts the vendor's office. Turn it off and late visits simply stay open."],
            ["auto_approve", "Approve automatically when everything is ticked and photographed", "Off means a person always signs off. On lets routine lists close themselves — anything short of a clean sweep still waits for you."],
            ["board_can_view", "Board members can open a visit you share", "Read-only, and only for visits you explicitly share. Board members can never comment to the vendor."]].map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 cursor-pointer">
              <span className="pt-0.5"><Toggle testid={`checklists-${k}`} checked={!!cl[k]} onChange={(v) => setCl({ ...cl, [k]: v })} /></span>
              <span>
                <span className="block text-sm font-semibold">{label}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
        </div>

        <SaveBtn onClick={() => saveChecklists()} busy={saving === "checklists"} />
      </ModuleCard>}
    </div>
  );
}
